// Picks the alpha-capable file for this browser. <source> ordering can't do it: Chrome on macOS
// decodes HEVC but drops its alpha (black box), and Safari's VP9-alpha WebM is unreliable.
function prefersHevc() {
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  const desktopSafari = /Safari/.test(ua) && !/Chrome|Chromium|CriOS|FxiOS|Edg|OPR|Android/.test(ua);
  return iOS || desktopSafari;
}

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

// Plays while it is both `data-active` and on screen; a controller (the hero slider) can toggle
// `data-active`, standalone uses just leave it set. The src is only attached on the first real
// play, so off-screen or inactive videos don't download anything.
class AlphaVideo extends HTMLElement {
  static observedAttributes = ['data-active'];

  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    const hevc = this.dataset.hevc;
    const webm = this.dataset.webm;
    this.src = (prefersHevc() ? hevc || webm : webm || hevc) || '';
    if (!this.src) return this.showPoster();

    this.video.addEventListener('error', () => this.showPoster());
    // `autoplay` can fire after the video went inactive or off screen; hold it to the rules.
    this.video.addEventListener('play', () => {
      if (!this.shouldPlay) this.video.pause();
    });

    this.onMotionChange = () => this.sync();
    reducedMotion.addEventListener('change', this.onMotionChange);

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.visible = entry.isIntersecting;
        this.sync();
      },
      { rootMargin: '100px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.onMotionChange) reducedMotion.removeEventListener('change', this.onMotionChange);
  }

  attributeChangedCallback() {
    this.sync();
  }

  get shouldPlay() {
    return this.hasAttribute('data-active') && this.visible && !reducedMotion.matches;
  }

  sync() {
    const video = this.video;
    if (!video || !this.src || this.failed) return;

    if (!this.shouldPlay) {
      video.pause();
      return;
    }

    if (!video.getAttribute('src')) {
      video.preload = 'auto';
      video.src = this.src;
      video.load();
    }
    video.play().catch(() => {});
  }

  // Swap the video for its poster PNG so a decode/network failure never leaves a black box or a gap.
  showPoster() {
    this.failed = true;
    const video = this.video;
    if (!video) return;
    const poster = video.getAttribute('poster');
    if (!poster) {
      video.hidden = true;
      return;
    }
    const img = document.createElement('img');
    img.className = 'alpha-video__poster';
    img.src = poster;
    img.alt = video.getAttribute('aria-label') || '';
    img.width = video.width;
    img.height = video.height;
    video.replaceWith(img);
    this.video = null;
  }
}

if (!customElements.get('alpha-video')) {
  customElements.define('alpha-video', AlphaVideo);
}
